import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel
} from "@material-ui/core";
import {
  StyledComponentProps,
  withStyles,
  WithStyles,
  withTheme,
  WithTheme
} from "@material-ui/core/styles";

import { ChangeEvent, Component, KeyboardEvent, ReactChild } from "react";
import * as React from "react";

interface IProps extends StyledComponentProps {
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  date?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  id?: string;
  label: string;
  missing?: boolean;
  leftItem?: ReactChild | string;
  multiline?: boolean;
  required?: boolean;
  rightItem?: ReactChild | string;
  type?: HTMLInputElement["type"];
  value?: string | number;
  maxLength?: number; //E-001
  variant?: string;
  numType?: string;
  onChange?(event: ChangeEvent<{}>, value: string): void;
  onBlur?(): void;
  onKeyPress?(event: any): void;

  onAddGroup?(): void;
}

/**
 * Pass in something that should be a function, and get a function back
 *
 * If you pass in a function, it will be returned
 *
 * If you pass in something falsey, then a new function will be returned.
 *
 * This ensures that whatever you wrap with this function will be a callable
 * result, and will not crash your program.
 *
 * @param callback The function to call
 */

// tslint:disable-next-line:ban-types
export function callable<T extends Function>(callback: T | undefined): T {
  return callback || (((): any => null) as any);
}

const styles = ({
  formControl: {
    background: "#fff",
    border: "solid 1px rgba(0, 0, 0, 0.1)",
    borderRadius: "2px",
    fontSize: "14px",
    height: "100%",
    minHeight: 50,
    padding: "0 10px",
    width: "100%"
  },
  formControlDisabled: {
    color: "#aaa"
  },
  formControlError: {
    border: "1px solid #d81b60"
  },
  helperText: {
    color: "#d81b60",
    position: "absolute" as "absolute",
    top: 50
  },
  inputLabel: {
    padding: "0px 5px",
    transform: "translate(0, 10px) scale(1)"
  },
  // tslint:disable-next-line:object-literal-sort-keys
  inputFocused: {
    border: "1px solid rgba(24, 194, 115, 0.5)"
  },
  inputLabelShrink: {
    transform: "translate(0, 1px) scale(0.8)"
  },
  inputRoot: {
    height: "100%",
    left: 0,
    minHeight: 50,
    padding: "15px 0 0 10px",
    position: "absolute" as "absolute",
    top: -16
  },
  root: {}
});

const decorate = withStyles(theme => ({
  formControl: {
    background: "#fff",
    border: "solid 1px rgba(0, 0, 0, 0.1)",
    borderRadius: "2px",
    fontSize: "14px",
    height: "100%",
    minHeight: 50,
    padding: "0 10px",
    width: "100%"
  },
  formControlDisabled: {
    color: "#aaa"
  },
  formControlError: {
    border: "1px solid #d81b60"
  },
  helperText: {
    color: "#d81b60",
    position: "absolute" as "absolute",
    top: 50
  },
  inputLabel: {
    padding: "0px 5px",
    transform: "translate(0, 10px) scale(1)"
  },
  // tslint:disable-next-line:object-literal-sort-keys
  inputFocused: {
    border: "1px solid rgba(24, 194, 115, 0.5)"
  },
  inputLabelShrink: {
    transform: "translate(0, 1px) scale(0.8)"
  },
  inputRoot: {
    height: "100%",
    left: 0,
    minHeight: 50,
    padding: "15px 0 0 10px",
    position: "absolute" as "absolute",
    top: -16
  },
  root: {}
}));

class MaterialTextFieldUnstyled extends Component<
  IProps &
    WithTheme &
    WithStyles<
      | "formControl"
      | "formControlDisabled"
      | "formControlError"
      | "helperText"
      | "inputFocused"
      | "inputLabel"
      | "inputLabelShrink"
      | "inputRoot"
      | "root"
    >
> {
  public render() {
    const {
      className = "",
      classes,
      disabled,
      error,
      label,
      leftItem,
      multiline = false,
      helperText,
      required,
      rightItem,
      value,
      maxLength, //E-001
      missing
    } = this.props;
    const hasIcon = leftItem ? "has-icon" : "";
    const hasError = error ? "material-text-field--error" : "";
    const isMissing = missing ? "material-text-field--missing" : "";
    return (
      <FormControl
        classes={{
          root: classes.formControl
        }}
        error={error}
        disabled={disabled}
        required={required}
        className={`material-text-field ${className} ${hasIcon} ${hasError} ${isMissing}`}
      >
        {leftItem && (
          <div className="material-text-field__icon">{leftItem}</div>
        )}
        <InputLabel
          classes={{
            root: classes.inputLabel,
            shrink: classes.inputLabelShrink
          }}
        >
          {label}
        </InputLabel>
        <Input
          autoFocus={this.props.autoFocus}
          onBlur={this.props.onBlur} // #TK added onBlur to this.props
          classes={{
            error: classes.formControlError,
            focused: classes.inputFocused,
            root: classes.inputRoot
          }}
          disableUnderline={true}
          fullWidth={true}
          multiline={multiline}
          onChange={this.handleOnChange}
          onKeyDown={this.handleKeyDown}
          type={this.props.type || "text"}
          value={value}
          placeholder={this.props.placeholder}
        />
        {rightItem ? (
          <div className="material-text-field__right-item">{rightItem}</div>
        ) : null}
        <FormHelperText
          classes={{
            root: classes.helperText
          }}
        >
          {helperText}
        </FormHelperText>
      </FormControl>
    );
  }
  private handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;

    if (value !== "") {
      if (this.props.type === "number" && this.props.numType === "integer") {
        value = String(parseInt(value, 10));
      }

      if (this.props.numType === "decimal") {
        value = value.replace(/[^0-9\.]/g, "");
        value = String(parseFloat(value));
      } else if (this.props.numType === "price") {
        value = value.replace(/[^0-9\.]/g, "");
        let precision = value
          .split(".")
          .slice(1)
          .join().length;
        if (precision > 2) {
          precision = 2;
        }
        if (precision > 0) {
          value = String(
            (Math.trunc(parseFloat(value) * 100) / 100).toFixed(precision)
          );
        }
      }
    }

    callable(this.props.onChange)(event, value);
  };

  private handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (this.props.type === "number" && this.props.numType === "integer") {
      if (event.key === ".") {
        event.preventDefault();
      }
    }
  };
}

// export const MaterialTextField = withTheme()(
//   decorate(MaterialTextFieldUnstyled)
// );

export const MaterialTextField = withStyles(styles, { withTheme: true })(MaterialTextFieldUnstyled);


// WEBPACK FOOTER //
// ./src/components/text-field/text-field.tsx